import React, { useState, useEffect } from "react";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { TextField } from "../../../../ui/components/TextField";
import {
  FeatherCheck,
  FeatherExternalLink,
  FeatherCheckCircle2,
  FeatherInfo,
  FeatherXCircle,
  FeatherClock,
  FeatherCalendar,
} from "@subframe/core";
import {
  ChecklistItemEntity,
  ChecklistAttachmentEntity,
} from "../../domain/entities/ChecklistEntity";
import { ProofUploadSection } from "./ProofUploadSection";

export type ChecklistItemCardVariant = "pending" | "approved" | "sentBack";

export interface ChecklistItemCardProps {
  item: ChecklistItemEntity;
  onViewTaskGuidance: () => void;
  onMarkDone: (itemId: number, notes?: string) => Promise<void>;
  /** When set, shows "To be done later" for eligible pending items. */
  onMarkToBeDoneLater?: (itemId: number) => Promise<void>;
  onUploadAttachment: (
    itemId: number,
    file: File
  ) => Promise<ChecklistAttachmentEntity>;
  onDeleteAttachment?: (attachmentId: number) => Promise<void>;
  hasPermission: boolean;
  requiresReview: boolean;
  loading?: boolean;
  disabled?: boolean;
  variant?: ChecklistItemCardVariant;
}

const getStatusBadgeVariant = (
  status: ChecklistItemEntity["status"]
): "neutral" | "warning" | "success" | "error" => {
  switch (status) {
    case "not_started":
      return "neutral";
    case "in_progress":
      return "warning";
    case "done":
    case "approved":
    case "to_be_done_later":
      return "success";
    case "submitted_for_review":
      return "warning";
    case "rejected":
      return "error";
    default:
      return "neutral";
  }
};

const getStatusLabel = (status: ChecklistItemEntity["status"]): string => {
  switch (status) {
    case "not_started":
      return "Not Started";
    case "in_progress":
      return "In Progress";
    case "done":
      return "Done";
    case "submitted_for_review":
      return "Submitted for Review";
    case "approved":
      return "Done";
    case "to_be_done_later":
      return "To be done later";
    case "rejected":
      return "Rejected";
    default:
      return status;
  }
};

export const ChecklistItemCard: React.FC<ChecklistItemCardProps> = ({
  item,
  onViewTaskGuidance,
  onMarkDone,
  onMarkToBeDoneLater,
  onUploadAttachment,
  onDeleteAttachment,
  hasPermission,
  requiresReview,
  loading = false,
  disabled = false,
  variant = "pending",
}) => {
  const [notes, setNotes] = useState(item.notes || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<
    number | null
  >(null);
  const [isDeferring, setIsDeferring] = useState(false);
  const [localAttachments, setLocalAttachments] = useState<
    ChecklistAttachmentEntity[]
  >(item.attachments || []);
  const trimmedItemNotes = item.notes?.trim();
  const hasItemNotes = Boolean(trimmedItemNotes);
  const showEditableNotes = variant === "pending" || variant === "sentBack";
  const showReadOnlyNotes = !showEditableNotes && hasItemNotes;

  // Update local attachments when item prop changes (e.g., from parent refresh)
  useEffect(() => {
    setLocalAttachments(item.attachments || []);
  }, [item.attachments]);

  const handleMarkDone = async () => {
    if (!hasPermission) return;
    setIsSubmitting(true);
    try {
      await onMarkDone(item.id, notes.trim() || undefined);
    } catch (error) {
      console.error("Error marking item as done:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const newAttachment = await onUploadAttachment(item.id, file);
      // Update local state with the new attachment instead of refetching
      setLocalAttachments((prev) => [...prev, newAttachment]);
    } catch (error) {
      console.error("Error uploading attachment:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (attachmentId: number) => {
    if (!onDeleteAttachment) return;
    setDeletingAttachmentId(attachmentId);
    try {
      await onDeleteAttachment(attachmentId);
      // Update local state by removing the deleted attachment
      setLocalAttachments((prev) =>
        prev.filter((att) => att.id !== attachmentId)
      );
    } catch (error) {
      console.error("Error deleting attachment:", error);
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const isDone = item.status === "done" || item.status === "approved";
  const isDeferred = item.status === "to_be_done_later";
  const isCompleted = isDone || isDeferred;
  const isPending = item.status === "submitted_for_review";
  const isRejected = item.status === "rejected";

  // Allow proof updates until the task is truly done; deferred items can still be completed later.
  const canEdit = hasPermission && !isDone && !isPending && !disabled;

  // Check if proof is required and has been uploaded (use local attachments)
  const hasAttachments = localAttachments && localAttachments.length > 0;
  const proofRequired = item.requires_proof;
  const hasProof = !proofRequired || hasAttachments;
  const canDefer = Boolean(onMarkToBeDoneLater) && !isPending && !disabled;

  // Create a modified item object with local attachments for ProofUploadSection
  const itemWithLocalAttachments = {
    ...item,
    attachments: localAttachments,
  };

  return (
    <div className="flex w-full flex-col items-start gap-3 rounded-md border border-solid border-neutral-border bg-default-background shadow-sm">
      <div className="flex w-full items-start justify-between px-4 pt-4">
        <div className="flex flex-col items-start gap-2">
          <div className="flex items-center gap-2">
            <span className="text-heading-3 font-heading-3 text-default-font">
              {item.title}
            </span>
            <Badge variant={getStatusBadgeVariant(item.status)}>
              {getStatusLabel(item.status)}
            </Badge>
            {item.priority ? (
              <Badge variant="neutral">
                Priority: {item.priority.toUpperCase()}
              </Badge>
            ) : null}
          </div>
          <span className="text-body font-body text-subtext-color text-sm">
            {item.description}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="neutral-secondary"
            size="small"
            icon={<FeatherInfo />}
            onClick={onViewTaskGuidance}
          >
            View Task Guidance
          </Button>
          {item.link_url && (
            <Button
              variant="neutral-secondary"
              size="small"
              icon={<FeatherExternalLink />}
              onClick={(e) => {
                e.stopPropagation();
                window.open(item.link_url, "_blank");
              }}
            >
              {item.link_text || "Go to Task"}
            </Button>
          )}
        </div>
      </div>
      <div className="flex h-px w-full flex-none flex-col items-center gap-2 bg-neutral-border" />
      <div className="flex w-full flex-col items-start gap-4 px-4 pb-4">
        {/* Show rejection feedback before correction controls */}
        {isRejected && item.review_comments && (
          <div className="flex w-full flex-col items-start gap-1 rounded-md bg-error-50 px-3 py-2 border border-solid border-error-200 text-sm">
            <div className="flex items-center gap-2">
              <FeatherXCircle className="text-body font-body text-error-600 text-sm" />
              <span className="text-body-bold font-body-bold text-error-700 text-sm">
                Review Rejected
              </span>
            </div>
            <div className="flex flex-col items-start gap-1 pl-5">
              <span className="text-body font-body text-error-700 text-sm">
                <strong>Rejection Reason:</strong> {item.review_comments}
              </span>
              {item.reviewed_at && (
                <span className="text-caption font-caption text-error-600">
                  Rejected on{" "}
                  {new Date(item.reviewed_at).toLocaleString("en-US", {
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </span>
              )}
            </div>
          </div>
        )}

        {item.requires_proof && (
          <ProofUploadSection
            item={itemWithLocalAttachments}
            onUpload={handleUpload}
            onDelete={handleDelete}
            uploading={uploading}
            deletingAttachmentId={deletingAttachmentId}
            disabled={!canEdit}
            readOnly={variant === "approved" && !isDeferred}
          />
        )}

        {showEditableNotes ? (
          <div className="flex w-full flex-col items-start gap-2">
            <span className="text-body-bold font-body-bold text-default-font text-sm">
              Notes (Optional)
            </span>
            <TextField
              className="h-auto w-full flex-none"
              variant="outline"
              label=""
              helpText=""
              icon={null}
              iconRight={null}
            >
              <TextField.Input
                placeholder="Add context or explanation for the reviewer"
                value={notes}
                onChange={(e) => {
                  e.stopPropagation();
                  setNotes(e.target.value);
                }}
                disabled={!canEdit}
                className="text-sm"
              />
            </TextField>
          </div>
        ) : showReadOnlyNotes ? (
          <div className="flex w-full flex-col items-start gap-1">
            <span className="text-body font-body text-subtext-color text-sm whitespace-pre-wrap">
              {trimmedItemNotes}
            </span>
          </div>
        ) : null}

        {/* Show pending message */}
        {isPending && (
          <div className="flex w-full flex-col items-start gap-1 rounded-md bg-warning-50 px-3 py-2 border border-solid border-warning-200 text-sm">
            <div className="flex items-center gap-2">
              <FeatherClock className="text-body font-body text-warning-600 text-sm" />
              <span className="text-body-bold font-body-bold text-warning-700 text-sm">
                Pending Review
              </span>
            </div>
            <div className="flex flex-col items-start gap-1 pl-5">
              <span className="text-body font-body text-warning-700 text-sm">
                This item has been submitted for review. No actions can be taken
                until it is reviewed.
              </span>
              {item.updated_at && (
                <span className="text-caption font-caption text-warning-600">
                  Submitted on{" "}
                  {new Date(item.updated_at).toLocaleString("en-US", {
                    month: "short",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Show completion message */}
        {isCompleted && item.status === "to_be_done_later" && (
          <div className="flex w-full flex-col items-start gap-1 rounded-md bg-success-50 px-3 py-2 text-sm">
            <div className="flex items-center gap-2">
              <FeatherCalendar className="text-body font-body text-success-600 text-sm" />
              <span className="text-body font-body text-success-700 text-sm">
                Marked as to be done later (counts toward month close).
              </span>
            </div>
          </div>
        )}

        {isCompleted && item.completed_at && item.status !== "to_be_done_later" && (
          <div className="flex w-full flex-col items-start gap-1 rounded-md bg-success-50 px-3 py-2 text-sm">
            <div className="flex items-center gap-2">
              <FeatherCheckCircle2 className="text-body font-body text-success-600 text-sm" />
              <span className="text-body font-body text-success-700 text-sm">
                Marked done on{" "}
                {new Date(item.completed_at).toLocaleString("en-US", {
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}
              </span>
            </div>
          </div>
        )}

        {/* Mark as Done / defer — deferred and rejected items can still be completed later. */}
        {item.status !== "approved" &&
          item.status !== "done" && (
          <div className="flex w-full flex-col items-start gap-1.5">
            <Button
              icon={<FeatherCheck />}
              onClick={(e) => {
                e.stopPropagation();
                if (hasPermission && hasProof) {
                  handleMarkDone();
                }
              }}
              disabled={
                !hasPermission ||
                !hasProof ||
                isSubmitting ||
                loading ||
                isDone ||
                isPending
              }
              className="w-full sm:w-auto"
            >
              {requiresReview ? "Send for review" : "Mark as done"}
            </Button>
            {onMarkToBeDoneLater &&
              (variant === "pending" || variant === "sentBack") && (
              <Button
                variant="neutral-secondary"
                icon={<FeatherCalendar />}
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!canDefer) return;
                  setIsDeferring(true);
                  try {
                    await onMarkToBeDoneLater(item.id);
                  } finally {
                    setIsDeferring(false);
                  }
                }}
                disabled={
                  !canDefer ||
                  isSubmitting ||
                  isDeferring ||
                  loading ||
                  disabled
                }
                className="w-full sm:w-auto"
              >
                To be Done later
              </Button>
            )}
            {!hasPermission && !disabled && (
              <span className="text-caption font-caption text-error-600">
                You do not have permission to mark this item as done
              </span>
            )}
            {disabled && (
              <span className="text-caption font-caption text-error-600">
                Cannot perform actions on items when the month is closed
              </span>
            )}
            {hasPermission && proofRequired && !hasAttachments && (
              <span className="text-caption font-caption text-warning-600">
                Please upload proof/screenshot before marking as done
              </span>
            )}
            {isPending && (
              <span className="text-caption font-caption text-warning-600">
                This item is pending review. Please wait for approval.
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
